/**
 * useSearchCandidates — TanStack Query hook for the All Candidates table.
 *
 * Mirrors `useSearchReport`'s lifecycle deliberately: the table is served from
 * the SAME compose as the report, so the two can be waiting on the same job,
 * and having them poll by different rules would show a finished report beside a
 * table that had given up (or the reverse).
 *
 * Three things stop the loop, and they are the report hook's three:
 *
 * - a `refresh_after_seconds` of 0 — the server saying asking again cannot
 *   change the answer;
 * - a TERMINAL error (404), which never becomes a 200 by asking again;
 * - the poll BUDGET, so a DLQ'd compose does not leave an open tab asking
 *   forever under a caption promising "a few seconds".
 *
 * What it does NOT do is poll a `ready` answer. A page of a composed projection
 * is final until the reader changes a filter or presses Refresh — the query key
 * carries the filters, so a filter change is a new query rather than a refetch.
 */

import type { SearchCandidatesResponse } from "@nu-assist/contracts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { useRef } from "react";

import type {
  SearchCandidatesQuery,
  SearchReportClient,
} from "../../../clients/searchReport.client";
import { isTerminalReportError, MAX_POLL_ATTEMPTS } from "./useSearchReport";

/** Fallback cadence when the payload omits an interval (older API). */
const FALLBACK_POLL_MS = 3000;

export interface UseSearchCandidatesArgs {
  client: SearchReportClient;
  /** Thrive search id from the route. Empty disables the query entirely. */
  searchId: string;
  /** Filters, sort and page — part of the query key, so a change is a new query. */
  query: SearchCandidatesQuery;
}

export interface UseSearchCandidatesResult {
  query: UseQueryResult<SearchCandidatesResponse>;
  /** The budget ran out while the payload was still asking to be polled. */
  pollingExhausted: boolean;
  /** Manual exit from a stalled state: restores the budget and asks again. */
  retry: () => void;
}

/**
 * Milliseconds until the next poll, or `false` to stop.
 *
 * Exported for its own unit test — the same reasoning `useSearchReport` gives:
 * driving TanStack's timers to assert this would be far more fragile than
 * calling the function.
 */
export function candidatesPollInterval(
  data: SearchCandidatesResponse | undefined,
): number | false {
  if (!data) return false;
  const seconds = data.refresh_after_seconds;
  if (typeof seconds === "number" && seconds > 0) return seconds * 1000;
  // A `generating` payload from an API that omits the interval still has to be
  // polled; anything else with a 0 means stop.
  return data.status === "generating" ? FALLBACK_POLL_MS : false;
}

/** The composed stop rule: budget and terminal errors both outrank the payload. */
export function nextCandidatesPollInterval(args: {
  data: SearchCandidatesResponse | undefined;
  error: unknown;
  attempts: number;
  maxAttempts?: number;
}): number | false {
  const max = args.maxAttempts ?? MAX_POLL_ATTEMPTS;
  if (isTerminalReportError(args.error)) return false;
  if (args.attempts >= max) return false;
  return candidatesPollInterval(args.data);
}

/**
 * A stable cache key for one set of filters.
 *
 * Built from the same params the request sends, so two states that produce the
 * same request share a cache entry and two that do not, do not. Sorting is what
 * makes it stable — `URLSearchParams` preserves insertion order, and a key that
 * depended on the order the reader happened to tick the filters in would miss
 * every cache hit.
 */
export function candidatesQueryKey(query: SearchCandidatesQuery): string {
  return JSON.stringify({
    page: query.page,
    pageSize: query.pageSize,
    sort: query.sort,
    order: query.order,
    name: query.name?.trim() || undefined,
    title: query.title?.trim() || undefined,
    company: query.company?.trim() || undefined,
    stage: [...(query.stage ?? [])].sort(),
    highestStage: [...(query.highestStage ?? [])].sort(),
    hasLinkedin: query.hasLinkedin,
    hasEmail: query.hasEmail,
    hasAssessment: query.hasAssessment,
    hasReferences: query.hasReferences,
    // Sorted like `stage`: two callers that list the same kinds in another
    // order must share one cache entry (/pr-review #1092 iter 1).
    reportKinds: query.reportKinds ? [...query.reportKinds].sort().join(",") : undefined,
  });
}

export function useSearchCandidates({
  client,
  searchId,
  query,
}: UseSearchCandidatesArgs): UseSearchCandidatesResult {
  const key = candidatesQueryKey(query);
  // Requests spent on THIS set of filters. A ref rather than TanStack's own
  // counters, for the reason `useSearchReport` documents: those live on the
  // cached query and would outlive the visit.
  const spent = useRef(0);
  const budgetFor = useRef(`${searchId}:${key}`);
  if (budgetFor.current !== `${searchId}:${key}`) {
    budgetFor.current = `${searchId}:${key}`;
    spent.current = 0;
  }

  const result = useQuery<SearchCandidatesResponse>({
    queryKey: ["searches", "candidates", searchId, key],
    queryFn: () => {
      spent.current += 1;
      return client.listCandidates(searchId, query);
    },
    enabled: Boolean(searchId),
    refetchInterval: (q) =>
      nextCandidatesPollInterval({
        data: q.state.data,
        error: q.state.error,
        attempts: spent.current,
      }),
    // Keep the previous page on screen while the next one loads, so paging does
    // not flash an empty table between two full ones.
    placeholderData: (prev) => prev,
    retry: (failureCount, error) => !isTerminalReportError(error) && failureCount < 3,
  });

  return {
    query: result,
    pollingExhausted:
      spent.current >= MAX_POLL_ATTEMPTS && candidatesPollInterval(result.data) !== false,
    retry: () => {
      spent.current = 0;
      void result.refetch();
    },
  };
}
